/**
 * The real-world analytics singleton (cue-forge-do7.1). Wires {@link createAnalytics}
 * to the configured endpoint, real localStorage, a beacon-preferring transport,
 * and real timers/crypto. Side-effect-light and safe to import without a DOM
 * (every browser global is guarded), so jsdom tests and non-browser builds do
 * not explode on import.
 *
 * Call sites (a later task) use the bound {@link track}; the opt-out toggle UI
 * uses {@link isAnalyticsOptedOut} / {@link setAnalyticsOptOut}.
 */
import { ANALYTICS_URL } from '../../config/analytics';
import { createAnalytics, type Analytics } from './analytics';
import type { EventName, EventProps } from './catalog';

const INERT_STORAGE: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

function realStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    // access can throw where storage is disabled (some private modes).
  }
  return INERT_STORAGE;
}

/**
 * Prefer `navigator.sendBeacon` (a CORS-simple request, survives page unload);
 * fall back to keepalive fetch. Fire-and-forget — the response is never read.
 */
function realSend(url: string, body: string): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    if (typeof fetch === 'function') {
      void fetch(url, { method: 'POST', body, keepalive: true }).catch(() => {});
    }
  } catch {
    // never surface transport errors into app code.
  }
}

function realUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the manual generator.
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const analytics: Analytics = createAnalytics({
  endpoint: ANALYTICS_URL,
  storage: realStorage(),
  send: realSend,
  now: () => Date.now(),
  uuid: realUuid,
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (t) => {
    if (t !== null && t !== undefined) clearTimeout(t as ReturnType<typeof setTimeout>);
  },
});

/** Bound convenience for call sites (typed against the event catalog). */
export function track<N extends EventName>(name: N, props: EventProps[N]): void {
  analytics.track(name, props);
}

/** Consent helpers for the opt-out toggle UI. */
export function isAnalyticsOptedOut(): boolean {
  return analytics.isOptedOut();
}
export function setAnalyticsOptOut(v: boolean): void {
  analytics.setOptOut(v);
}

// Best-effort flush on page dismissal; guarded for non-browser environments.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => analytics.flush());
}
